import React, { createContext, useCallback, useState } from 'react';
import _get from 'lodash/get';

import { useFetch, useForm, useMutation, useSegment } from 'utils/hooks';
import { createPayload } from './utils';
import { DEFAULT_FORM_DATA, FORM_SCHEMA } from './stubs';

/**
 * @type {React.Context<{
 *  mode: import('utils/types').FormMode
 *  data: import('zod').infer<typeof FORM_SCHEMA>,
 *  initialData: import('zod').infer<typeof FORM_SCHEMA>,
 *  errors: Partial<import('zod').infer<typeof FORM_SCHEMA>>,
 *  isDirty: boolean,
 *  isError: boolean,
 *  isLoading: boolean,
 *  isFormOpen: boolean,
 *  isDisabled: boolean,
 *  isProcessing: boolean,
 *  onLoad: () => void,
 *  onChange: (updates: Partial<import('zod').infer<typeof FORM_SCHEMA>>, config?: import('utils/types').FormValidationConfig) => void,
 *  onFormOpen: () => void,
 *  onFormClose: () => void,
 *  onEnableForm: () => void,
 *  onDisableForm: () => void,
 *  onSave: () => void,
 * }}
 */
const RequestReasonConfigContext = createContext();

export const useRequestReasonConfig = () => {
  const context = React.useContext(RequestReasonConfigContext);
  if (!context) {
    throw new Error(
      'useRequestReasonConfig must be used within a RequestReasonConfigProvider'
    );
  }
  return context;
};

export function RequestReasonConfigProvider({
  reasonId = '',
  onSaved = () => {},
  children = null
}) {
  const { track } = useSegment();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  /** @type {import('utils/types').FormMode} */
  const mode = reasonId ? 'edit' : 'create';

  const {
    data,
    initialData,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: DEFAULT_FORM_DATA
  });

  const { isLoading, isError, fetchData: onLoad } = useFetch(
    'configure.reasons.root',
    {
      loadOnMount: false,
      routeParam: reasonId,
      initialData: DEFAULT_FORM_DATA,
      onTransform: data => _get(data, 'request_reasons', DEFAULT_FORM_DATA),
      onSuccess: onSetInitialData
    }
  );

  const { mutate, isProcessing } = useMutation(
    'configure.reasons.root',
    mode === 'create' ? 'POST' : 'PUT',
    {
      routeParam: reasonId,
      payload: () => createPayload(data),
      successMessage: 'Successfully saved the request reason.',
      errorMessage: 'Failed to save the request reason.',
      onValidate: () => onValidate(data),
      onSuccess: () => {
        if (mode === 'create') {
          track('FTU Wizard Return Reason Created', {
            reason_name: data.name,
            allows_sub_reasons: data.allows_sub_reasons,
            sub_reasons_count: data.sub_reasons_data?.sub_reasons?.length || 0
          });
        } else {
          track('FTU Wizard Return Reason Edited', {
            reason_id: reasonId,
            reason_name: data.name,
            allows_sub_reasons: data.allows_sub_reasons,
            sub_reasons_count: data.sub_reasons_data?.sub_reasons?.length || 0
          });
        }
        setIsFormOpen(false);
        onSaved();
      }
    }
  );

  const onSave = useCallback(() => {
    if (!isDirty && mode === 'edit') {
      return setIsFormOpen(false);
    }
    mutate();
  }, [isDirty, mode, mutate]);

  const onFormOpen = useCallback(() => {
    setIsFormOpen(true);
    if (!!reasonId) {
      onLoad();
    }
  }, [reasonId, onLoad]);

  const onEnableForm = useCallback(() => setIsDisabled(false), []);

  const onDisableForm = useCallback(() => setIsDisabled(true), []);

  const onFormClose = useCallback(() => setIsFormOpen(false), []);

  return (
    <RequestReasonConfigContext.Provider
      value={{
        mode,
        data,
        initialData,
        errors,
        isDirty,
        isError,
        isLoading,
        isFormOpen,
        isDisabled,
        isProcessing,
        onLoad,
        onChange,
        onFormOpen,
        onFormClose,
        onEnableForm,
        onDisableForm,
        onSave
      }}
    >
      {children}
    </RequestReasonConfigContext.Provider>
  );
}
